import { Button, Carousel, CarouselItem, Modal } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { MatchViewModel, MatchViewModelSchema } from '../../backend/entryPoints/mapper/mapToMatchViewModel';
import { saveMatchData } from '../../backend/entryPoints/saveMatchDataToServer';
import { getMatchData } from '../../backend/entryPoints/getMatchDataFromServer';
import { saveFieldAllocation } from '../../backend/entryPoints/saveFieldAllocation';
import { useTranslation } from 'react-i18next';

import './settings-style.css';

function MatchSettings(props: {
    field: string;
    modeString: string;
    match: MatchViewModel | undefined;
    setMatch: (match: MatchViewModel) => void;
    visible: boolean;
    interval: boolean;
    handleClose: () => void;
}) {
    const { t } = useTranslation();
    const [modeString, setModeString] = useState<string>('');
    const [servingTeam, setServingTeam] = useState<number>(-1);
    const [server, setServer] = useState<number>(-1);
    const [receiver, setReceiver] = useState<number>(-1);
    const [match, setMatch] = useState<MatchViewModel>();
    const [currentPage, setCurrentPage] = useState<number>(0);

    const pages = [
        { title: t('settings.selectGame.title') },
        { title: t('settings.servingTeam.title') },
        { title: t('settings.serverReceiver.title') },
        { title: t('settings.summary.title') },
    ];

    useEffect(() => {
        setModeString('');
        setServingTeam(-1);
        setServer(-1);
        setReceiver(-1);
        setMatch(undefined);
    }, [props.visible, props.modeString]);

    // reset to first page when opened
    useEffect(() => {
        setCurrentPage(0);
    }, [props.visible]);

    useEffect(() => {
        setServer(-1);
        setReceiver(-1);
    }, [servingTeam]);

    const onGameSelect = (gameMode: string) => {
        setModeString(gameMode);
        getMatchData(gameMode)
            .then((response) => response.text().then((data) => mapAndSetMatch(data)))
            .catch((err) => console.error(err));
    };

    const nextPage = () => {
        if (currentPage === 1 && !modeString.includes('D')) {
            // Skip server/receiver selection for singles
            setServer(servingTeam === 0 ? 0 : 2);
            setReceiver(servingTeam === 0 ? 2 : 0);
            setCurrentPage((prevPage) => Math.min(prevPage + 2, pages.length - 1));
            return;
        }
        setCurrentPage((prevPage) => Math.min(prevPage + 1, pages.length - 1));
    };
    const previousPage = () => {
        if (currentPage === 3 && !modeString.includes('D')) {
            // Skip server/receiver selection for singles
            setCurrentPage((prevPage) => Math.max(prevPage - 2, 0));
            return;
        }
        setCurrentPage((prevPage) => Math.max(prevPage - 1, 0));
    };
    const shouldNextBeDisabled = () => {
        if (currentPage === 0) return modeString === '';
        if (currentPage === 1) return servingTeam === -1;
        if (currentPage === 2) return server === -1 || receiver === -1;
        return false;
    };

    const mapAndSetMatch = (data: string) => {
        const matchData = MatchViewModelSchema.safeParse(JSON.parse(data));
        if (!matchData.success) {
            console.error('Error parsing match data: ', matchData.error);
            return;
        }
        setMatch(matchData.data);
    };

    // How to interpret the player positioning array:
    // [[odd, even], [even, odd]] starting with the home team as first element
    const determineInitialPlayerPositioning = () => {
        if (!modeString.includes('D')) {
            return [
                [-1, 0],
                [2, -1],
            ];
        }
        if (server === -1 || receiver === -1) {
            window.alert('Server or receiver is not set.');
            return [
                [-1, -1],
                [-1, -1],
            ];
        }
        const servingSide = servingTeam === 0 ? [(server + 1) % 2, server] : [server, ((server + 1) % 2) + 2];
        const receivingSide = servingTeam === 0 ? [receiver, ((receiver + 1) % 2) + 2] : [(receiver + 1) % 2, receiver];
        return servingTeam === 0 ? [servingSide, receivingSide] : [receivingSide, servingSide];
    };

    const handleSaveAndClose = () => {
        const playerPositioning = determineInitialPlayerPositioning();
        const saveMatchRequest: MatchViewModel = MatchViewModelSchema.parse({
            ...match,
            player_positioning: playerPositioning,
            serving_team: servingTeam,
            server,
            receiver,
        });
        saveMatchData(saveMatchRequest)
            .then((_) => {
                props.setMatch(saveMatchRequest);
            })
            .catch((err) => {
                window.alert('Error saving match data: ' + err.message);
            });
        saveFieldAllocation(props.field, modeString).then((r) => console.log(r));
        props.handleClose();
    };

    return (
        <Modal show={props.visible} onHide={props.handleClose} size={'xl'}>
            <Modal.Header closeButton={true}>
                <Modal.Title>{pages[currentPage].title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Carousel controls={false} indicators={false} activeIndex={currentPage}>
                    <CarouselItem>
                        <p>{t('settings.selectGame.content')}</p>
                        <table className={'width100'}>
                            <tbody>
                                <tr>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => onGameSelect('1.MD')}
                                            className={`settingsButton ${modeString === '1.MD' ? 'selection_button' : ''}`}
                                        >
                                            {t('general.games.1md')}
                                        </Button>
                                    </td>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => onGameSelect('2.MD')}
                                            className={`settingsButton ${modeString === '2.MD' ? 'selection_button' : ''}`}
                                        >
                                            {t('general.games.2md')}
                                        </Button>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => onGameSelect('WD')}
                                            className={`settingsButton ${modeString === 'WD' ? 'selection_button' : ''}`}
                                        >
                                            {t('general.games.wd')}
                                        </Button>
                                    </td>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => onGameSelect('XD')}
                                            className={`settingsButton ${modeString === 'XD' ? 'selection_button' : ''}`}
                                        >
                                            {t('general.games.xd')}
                                        </Button>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => onGameSelect('1.MS')}
                                            className={`settingsButton ${modeString === '1.MS' ? 'selection_button' : ''}`}
                                        >
                                            {t('general.games.1ms')}
                                        </Button>
                                    </td>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => onGameSelect('2.MS')}
                                            className={`settingsButton ${modeString === '2.MS' ? 'selection_button' : ''}`}
                                        >
                                            {t('general.games.2ms')}
                                        </Button>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => onGameSelect('3.MS')}
                                            className={`settingsButton ${modeString === '3.MS' ? 'selection_button' : ''}`}
                                        >
                                            {t('general.games.3ms')}
                                        </Button>
                                    </td>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => onGameSelect('WS')}
                                            className={`settingsButton ${modeString === 'WS' ? 'selection_button' : ''}`}
                                        >
                                            {t('general.games.ws')}
                                        </Button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </CarouselItem>
                    <CarouselItem>
                        <p>{t('settings.servingTeam.content')}</p>
                        <table className={'width100'}>
                            <tbody>
                                <tr>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => setServingTeam(0)}
                                            className={`settingsButton ${servingTeam === 0 ? 'selection_button' : ''}`}
                                        >
                                            {match?.home_team}
                                        </Button>
                                    </td>
                                    <td className={'td-padding'}>
                                        <Button
                                            size={'lg'}
                                            onClick={() => setServingTeam(1)}
                                            className={`settingsButton ${servingTeam === 1 ? 'selection_button' : ''}`}
                                        >
                                            {match?.guest_team}
                                        </Button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </CarouselItem>
                    <CarouselItem>
                        <p>{t('settings.serverReceiver.content')}</p>
                        {servingTeam !== undefined && match !== undefined ? (
                            <table className={'width100'}>
                                <tbody>
                                    <tr>
                                        <td className={'td-padding'}>
                                            <span>{t('settings.serverReceiver.select_server')}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className={'td-padding'}>
                                            <Button
                                                size={'lg'}
                                                className={`settingsButton ${
                                                    servingTeam === 0
                                                        ? server === 0
                                                            ? 'selection_button'
                                                            : ''
                                                        : server === 2
                                                          ? 'selection_button'
                                                          : ''
                                                }`}
                                                onClick={() => setServer(servingTeam === 0 ? 0 : 2)}
                                            >
                                                {servingTeam === 0 ? match.player_home[0] : match.player_guest[0]}
                                            </Button>
                                        </td>
                                        {modeString.includes('D') ? (
                                            <td className={'td-padding'}>
                                                <Button
                                                    size={'lg'}
                                                    className={`settingsButton ${
                                                        servingTeam === 0
                                                            ? server === 1
                                                                ? 'selection_button'
                                                                : ''
                                                            : server === 3
                                                              ? 'selection_button'
                                                              : ''
                                                    }`}
                                                    onClick={() => setServer(servingTeam === 0 ? 1 : 3)}
                                                >
                                                    {servingTeam === 0 ? match.player_home[1] : match.player_guest[1]}
                                                </Button>
                                            </td>
                                        ) : null}
                                    </tr>
                                    <tr>
                                        <td className={'td-padding'}>
                                            <span>{t('settings.serverReceiver.select_receiver')}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className={'td-padding'}>
                                            <Button
                                                size={'lg'}
                                                className={`settingsButton ${
                                                    servingTeam === 0
                                                        ? receiver === 2
                                                            ? 'selection_button'
                                                            : ''
                                                        : receiver === 0
                                                          ? 'selection_button'
                                                          : ''
                                                }`}
                                                onClick={() => setReceiver(servingTeam === 0 ? 2 : 0)}
                                            >
                                                {servingTeam === 0 ? match.player_guest[0] : match.player_home[0]}
                                            </Button>
                                        </td>
                                        {modeString.includes('D') ? (
                                            <td className={'td-padding'}>
                                                <Button
                                                    size={'lg'}
                                                    className={`settingsButton ${
                                                        servingTeam === 0
                                                            ? receiver === 3
                                                                ? 'selection_button'
                                                                : ''
                                                            : receiver === 1
                                                              ? 'selection_button'
                                                              : ''
                                                    }`}
                                                    onClick={() => setReceiver(servingTeam === 0 ? 3 : 1)}
                                                >
                                                    {servingTeam === 0 ? match.player_guest[1] : match.player_home[1]}
                                                </Button>
                                            </td>
                                        ) : null}
                                    </tr>
                                </tbody>
                            </table>
                        ) : null}
                    </CarouselItem>
                    <CarouselItem>
                        <p>{t('settings.summary.content')}</p>
                        <ul>
                            <li>
                                {t('general.serving_team')}:&nbsp;
                                {servingTeam === 0 ? match?.home_team : match?.guest_team}
                            </li>
                            <li>
                                {t('general.server')}:&nbsp;
                                {servingTeam === 0 ? match?.player_home[server] : match?.player_guest[server % 2]}
                            </li>
                            <li>
                                {t('general.receiver')}:&nbsp;
                                {servingTeam === 0 ? match?.player_guest[receiver % 2] : match?.player_home[receiver]}
                            </li>
                        </ul>
                    </CarouselItem>
                </Carousel>
            </Modal.Body>
            <Modal.Footer>
                {currentPage !== 0 ? <Button onClick={previousPage}>{t('general.previous')}</Button> : null}
                {currentPage !== pages.length - 1 ? (
                    <Button onClick={nextPage} disabled={shouldNextBeDisabled()}>
                        {t('general.next')}
                    </Button>
                ) : (
                    <Button onClick={handleSaveAndClose}>{t('general.confirm')}</Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default MatchSettings;
